// Plain English → Official NPI Taxonomy mapping
// Used by the "Find a Doctor" page to translate patient-friendly terms

export const DOCTOR_MAPPINGS = [
  {
    official_name: "Cardiologist",
    official_slug: "cardiologist",
    url_slug: "heart-doctor",
    taxonomy_description: "Cardiovascular Disease",
    layman_terms: ["heart doctor", "cardiac doctor", "heart specialist", "chest doctor"],
    condition_terms: ["heart disease", "chest pain", "high blood pressure", "palpitations"],
    color: "bg-red-50 text-red-600 border-red-200",
    browse_label: "Heart Doctor",
  },
  {
    official_name: "Dermatologist",
    official_slug: "dermatologist",
    url_slug: "skin-doctor",
    taxonomy_description: "Dermatology",
    layman_terms: ["skin doctor", "acne doctor", "rash doctor", "mole doctor"],
    condition_terms: ["acne", "eczema", "psoriasis", "rash", "skin cancer"],
    color: "bg-orange-50 text-orange-600 border-orange-200",
    browse_label: "Skin Doctor",
  },
  {
    official_name: "Ophthalmologist",
    official_slug: "ophthalmologist",
    url_slug: "eye-doctor",
    taxonomy_description: "Ophthalmology",
    layman_terms: ["eye doctor", "vision doctor", "glasses doctor"],
    condition_terms: ["blurry vision", "cataracts", "glaucoma", "eye infection"],
    color: "bg-blue-50 text-blue-600 border-blue-200",
    browse_label: "Eye Doctor",
  },
  {
    official_name: "Gastroenterologist",
    official_slug: "gastroenterologist",
    url_slug: "stomach-doctor",
    taxonomy_description: "Gastroenterology",
    layman_terms: ["stomach doctor", "gut doctor", "bowel doctor", "acid reflux doctor"],
    condition_terms: ["acid reflux", "ibs", "stomach pain", "bloating", "colonoscopy"],
    color: "bg-amber-50 text-amber-600 border-amber-200",
    browse_label: "Stomach Doctor",
  },
  {
    official_name: "Orthopedic Surgeon",
    official_slug: "orthopedic-surgeon",
    url_slug: "knee-doctor",
    taxonomy_description: "Orthopaedic Surgery",
    layman_terms: ["bone doctor", "joint doctor", "knee doctor", "hip doctor", "sports doctor"],
    condition_terms: ["broken bones", "knee pain", "hip pain", "sports injuries"],
    color: "bg-slate-100 text-slate-700 border-slate-300",
    browse_label: "Knee / Bone Doctor",
  },
  {
    official_name: "Neurologist",
    official_slug: "neurologist",
    url_slug: "brain-doctor",
    taxonomy_description: "Neurology",
    layman_terms: ["brain doctor", "nerve doctor", "headache doctor", "migraine doctor"],
    condition_terms: ["migraines", "epilepsy", "stroke", "nerve pain", "numbness"],
    color: "bg-purple-50 text-purple-600 border-purple-200",
    browse_label: "Brain Doctor",
  },
  {
    official_name: "Psychiatrist",
    official_slug: "psychiatrist",
    url_slug: "anxiety-doctor",
    taxonomy_description: "Psychiatry",
    layman_terms: ["mental health doctor", "anxiety doctor", "depression doctor", "adhd doctor"],
    condition_terms: ["anxiety", "depression", "bipolar", "adhd", "ptsd", "panic attacks"],
    color: "bg-indigo-50 text-indigo-600 border-indigo-200",
    browse_label: "Anxiety / Mental Health Doctor",
  },
  {
    official_name: "Endocrinologist",
    official_slug: "endocrinologist",
    url_slug: "diabetes-doctor",
    taxonomy_description: "Endocrinology",
    layman_terms: ["diabetes doctor", "thyroid doctor", "hormone doctor"],
    condition_terms: ["diabetes", "thyroid disease", "hormonal imbalance"],
    color: "bg-cyan-50 text-cyan-600 border-cyan-200",
    browse_label: "Diabetes / Thyroid Doctor",
  },
  {
    official_name: "Urologist",
    official_slug: "urologist",
    url_slug: "bladder-doctor",
    taxonomy_description: "Urology",
    layman_terms: ["kidney doctor", "bladder doctor", "uti doctor", "prostate doctor"],
    condition_terms: ["uti", "kidney stones", "prostate issues", "incontinence"],
    color: "bg-rose-50 text-rose-600 border-rose-200",
    browse_label: "Kidney / Bladder Doctor",
  },
  {
    official_name: "Pulmonologist",
    official_slug: "pulmonologist",
    url_slug: "lung-doctor",
    taxonomy_description: "Pulmonary Disease",
    layman_terms: ["lung doctor", "breathing doctor", "asthma doctor", "copd doctor"],
    condition_terms: ["asthma", "copd", "shortness of breath", "bronchitis"],
    color: "bg-sky-50 text-sky-600 border-sky-200",
    browse_label: "Lung / Breathing Doctor",
  },
  {
    official_name: "Rheumatologist",
    official_slug: "rheumatologist",
    url_slug: "arthritis-doctor",
    taxonomy_description: "Rheumatology",
    layman_terms: ["arthritis doctor", "joint pain doctor", "lupus doctor"],
    condition_terms: ["rheumatoid arthritis", "lupus", "fibromyalgia", "gout"],
    color: "bg-yellow-50 text-yellow-700 border-yellow-200",
    browse_label: "Arthritis Doctor",
  },
  {
    official_name: "Oncologist",
    official_slug: "oncologist",
    url_slug: "cancer-doctor",
    taxonomy_description: "Oncology",
    layman_terms: ["cancer doctor", "tumor doctor", "chemo doctor"],
    condition_terms: ["cancer", "tumors", "leukemia", "lymphoma"],
    color: "bg-pink-50 text-pink-600 border-pink-200",
    browse_label: "Cancer Doctor",
  },
  {
    official_name: "OB-GYN",
    official_slug: "obgyn",
    url_slug: "womens-doctor",
    taxonomy_description: "Obstetrics & Gynecology",
    layman_terms: ["womens doctor", "women's doctor", "pregnancy doctor", "maternity doctor"],
    condition_terms: ["pregnancy", "periods", "menopause", "pcos", "pelvic pain"],
    color: "bg-fuchsia-50 text-fuchsia-600 border-fuchsia-200",
    browse_label: "Women's / Pregnancy Doctor",
  },
  {
    official_name: "ENT Specialist",
    official_slug: "ent",
    url_slug: "ear-throat-doctor",
    taxonomy_description: "Otolaryngology",
    layman_terms: ["ear doctor", "throat doctor", "sinus doctor", "nose doctor"],
    condition_terms: ["ear infections", "sinusitis", "hearing loss", "snoring"],
    color: "bg-teal-50 text-teal-600 border-teal-200",
    browse_label: "Ear / Throat Doctor",
  },
  {
    official_name: "Allergist",
    official_slug: "allergist",
    url_slug: "allergy-doctor",
    taxonomy_description: "Allergy & Immunology",
    layman_terms: ["allergy doctor", "food allergy doctor", "hay fever doctor"],
    condition_terms: ["allergies", "asthma", "food allergies", "hives"],
    color: "bg-green-50 text-green-600 border-green-200",
    browse_label: "Allergy Doctor",
  },
  {
    official_name: "Podiatrist",
    official_slug: "podiatrist",
    url_slug: "foot-doctor",
    taxonomy_description: "Podiatrist",
    layman_terms: ["foot doctor", "ankle doctor", "toe doctor", "heel doctor"],
    condition_terms: ["plantar fasciitis", "bunions", "ingrown toenails", "ankle pain"],
    color: "bg-stone-100 text-stone-600 border-stone-300",
    browse_label: "Foot Doctor",
  },
  {
    official_name: "Pediatrician",
    official_slug: "pediatrician",
    url_slug: "childrens-doctor",
    taxonomy_description: "Pediatrics",
    layman_terms: ["childrens doctor", "children's doctor", "baby doctor", "kids doctor", "infant doctor"],
    condition_terms: ["childhood illnesses", "vaccines", "fever", "newborn care"],
    color: "bg-lime-50 text-lime-600 border-lime-200",
    browse_label: "Children's Doctor",
  },
  {
    official_name: "Pain Management Specialist",
    official_slug: "pain-management",
    url_slug: "pain-doctor",
    taxonomy_description: "Pain Medicine",
    layman_terms: ["pain doctor", "chronic pain doctor", "back pain doctor"],
    condition_terms: ["chronic pain", "sciatica", "nerve pain", "fibromyalgia"],
    color: "bg-red-50 text-red-500 border-red-200",
    browse_label: "Back / Chronic Pain Doctor",
  },
  {
    official_name: "Sleep Medicine Specialist",
    official_slug: "sleep-specialist",
    url_slug: "sleep-doctor",
    taxonomy_description: "Sleep Medicine",
    layman_terms: ["sleep doctor", "sleep apnea doctor", "insomnia doctor"],
    condition_terms: ["sleep apnea", "insomnia", "narcolepsy", "snoring"],
    color: "bg-violet-50 text-violet-600 border-violet-200",
    browse_label: "Sleep Doctor",
  },
  {
    official_name: "Infectious Disease Specialist",
    official_slug: "infectious-disease",
    url_slug: "infection-doctor",
    taxonomy_description: "Infectious Disease",
    layman_terms: ["std doctor", "infection doctor", "hiv doctor", "lyme doctor"],
    condition_terms: ["hiv", "stds", "lyme disease", "hepatitis", "tuberculosis"],
    color: "bg-emerald-50 text-emerald-600 border-emerald-200",
    browse_label: "Infection / STD Doctor",
  },
  {
    official_name: "Nephrologist",
    official_slug: "nephrologist",
    url_slug: "kidney-specialist",
    taxonomy_description: "Nephrology",
    layman_terms: ["kidney specialist", "renal doctor", "dialysis doctor"],
    condition_terms: ["chronic kidney disease", "kidney failure", "dialysis"],
    color: "bg-rose-50 text-rose-500 border-rose-200",
    browse_label: "Kidney Specialist",
  },
  {
    official_name: "Vascular Surgeon",
    official_slug: "vascular-surgeon",
    url_slug: "vein-doctor",
    taxonomy_description: "Vascular Surgery",
    layman_terms: ["vein doctor", "varicose vein doctor", "circulation doctor"],
    condition_terms: ["varicose veins", "blood clots", "poor circulation"],
    color: "bg-red-50 text-red-400 border-red-200",
    browse_label: "Vein / Circulation Doctor",
  },
  {
    official_name: "Hematologist",
    official_slug: "hematologist",
    url_slug: "blood-doctor",
    taxonomy_description: "Hematology",
    layman_terms: ["blood doctor", "blood disorder doctor", "blood specialist"],
    condition_terms: ["anemia", "leukemia", "clotting disorders", "sickle cell"],
    color: "bg-red-100 text-red-700 border-red-300",
    browse_label: "Blood Doctor",
  },
  {
    official_name: "Bariatric Physician",
    official_slug: "bariatric",
    url_slug: "weight-loss-doctor",
    taxonomy_description: "Bariatric Medicine",
    layman_terms: ["weight loss doctor", "obesity doctor", "diet doctor"],
    condition_terms: ["obesity", "weight management", "metabolic syndrome"],
    color: "bg-emerald-50 text-emerald-500 border-emerald-200",
    browse_label: "Weight Loss Doctor",
  },
  {
    official_name: "Family Medicine Physician",
    official_slug: "family-doctor",
    url_slug: "family-doctor",
    taxonomy_description: "Family Medicine",
    layman_terms: ["family doctor", "general doctor", "gp", "primary care doctor", "regular doctor"],
    condition_terms: ["general health", "checkups", "preventive care", "referrals"],
    color: "bg-blue-50 text-blue-500 border-blue-200",
    browse_label: "Family / General Doctor",
  },
  {
    official_name: "Dentist",
    official_slug: "dentist",
    url_slug: "teeth-doctor",
    taxonomy_description: "Dentist",
    layman_terms: ["teeth doctor", "dental doctor", "tooth doctor", "mouth doctor"],
    condition_terms: ["tooth decay", "gum disease", "toothache", "wisdom teeth"],
    color: "bg-sky-50 text-sky-500 border-sky-200",
    browse_label: "Teeth / Dental Doctor",
  },
  {
    official_name: "Chiropractor",
    official_slug: "chiropractor",
    url_slug: "spine-doctor",
    taxonomy_description: "Chiropractor",
    layman_terms: ["spine doctor", "back cracking doctor", "chiro"],
    condition_terms: ["back pain", "neck pain", "sciatica", "spinal misalignment"],
    color: "bg-emerald-50 text-emerald-600 border-emerald-200",
    browse_label: "Chiropractor / Spine Doctor",
  },
];

// Featured items for the browse grid
export const FEATURED_BROWSE = [
  "Heart Doctor", "Skin Doctor", "Eye Doctor", "Stomach Doctor", "Knee / Bone Doctor",
  "Brain Doctor", "Anxiety / Mental Health Doctor", "Children's Doctor",
  "Women's / Pregnancy Doctor", "Ear / Throat Doctor", "Lung / Breathing Doctor",
  "Allergy Doctor", "Foot Doctor", "Family / General Doctor",
  "Back / Chronic Pain Doctor", "Cancer Doctor", "Diabetes / Thyroid Doctor",
  "Arthritis Doctor", "Sleep Doctor", "Weight Loss Doctor",
];

// ============== STATE UTILITIES ==============

export const STATE_DATA = [
  { code: "AL", name: "Alabama", slug: "alabama" },
  { code: "AK", name: "Alaska", slug: "alaska" },
  { code: "AZ", name: "Arizona", slug: "arizona" },
  { code: "AR", name: "Arkansas", slug: "arkansas" },
  { code: "CA", name: "California", slug: "california" },
  { code: "CO", name: "Colorado", slug: "colorado" },
  { code: "CT", name: "Connecticut", slug: "connecticut" },
  { code: "DE", name: "Delaware", slug: "delaware" },
  { code: "FL", name: "Florida", slug: "florida" },
  { code: "GA", name: "Georgia", slug: "georgia" },
  { code: "HI", name: "Hawaii", slug: "hawaii" },
  { code: "ID", name: "Idaho", slug: "idaho" },
  { code: "IL", name: "Illinois", slug: "illinois" },
  { code: "IN", name: "Indiana", slug: "indiana" },
  { code: "IA", name: "Iowa", slug: "iowa" },
  { code: "KS", name: "Kansas", slug: "kansas" },
  { code: "KY", name: "Kentucky", slug: "kentucky" },
  { code: "LA", name: "Louisiana", slug: "louisiana" },
  { code: "ME", name: "Maine", slug: "maine" },
  { code: "MD", name: "Maryland", slug: "maryland" },
  { code: "MA", name: "Massachusetts", slug: "massachusetts" },
  { code: "MI", name: "Michigan", slug: "michigan" },
  { code: "MN", name: "Minnesota", slug: "minnesota" },
  { code: "MS", name: "Mississippi", slug: "mississippi" },
  { code: "MO", name: "Missouri", slug: "missouri" },
  { code: "MT", name: "Montana", slug: "montana" },
  { code: "NE", name: "Nebraska", slug: "nebraska" },
  { code: "NV", name: "Nevada", slug: "nevada" },
  { code: "NH", name: "New Hampshire", slug: "new-hampshire" },
  { code: "NJ", name: "New Jersey", slug: "new-jersey" },
  { code: "NM", name: "New Mexico", slug: "new-mexico" },
  { code: "NY", name: "New York", slug: "new-york" },
  { code: "NC", name: "North Carolina", slug: "north-carolina" },
  { code: "ND", name: "North Dakota", slug: "north-dakota" },
  { code: "OH", name: "Ohio", slug: "ohio" },
  { code: "OK", name: "Oklahoma", slug: "oklahoma" },
  { code: "OR", name: "Oregon", slug: "oregon" },
  { code: "PA", name: "Pennsylvania", slug: "pennsylvania" },
  { code: "RI", name: "Rhode Island", slug: "rhode-island" },
  { code: "SC", name: "South Carolina", slug: "south-carolina" },
  { code: "SD", name: "South Dakota", slug: "south-dakota" },
  { code: "TN", name: "Tennessee", slug: "tennessee" },
  { code: "TX", name: "Texas", slug: "texas" },
  { code: "UT", name: "Utah", slug: "utah" },
  { code: "VT", name: "Vermont", slug: "vermont" },
  { code: "VA", name: "Virginia", slug: "virginia" },
  { code: "WA", name: "Washington", slug: "washington" },
  { code: "WV", name: "West Virginia", slug: "west-virginia" },
  { code: "WI", name: "Wisconsin", slug: "wisconsin" },
  { code: "WY", name: "Wyoming", slug: "wyoming" },
  { code: "DC", name: "District of Columbia", slug: "district-of-columbia" },
];

export function getStateBySlug(slug) {
  return STATE_DATA.find(s => s.slug === slug) || null;
}

export function getStateByCode(code) {
  return STATE_DATA.find(s => s.code === code.toUpperCase()) || null;
}

export function stateCodeToSlug(code) {
  const st = getStateByCode(code);
  return st ? st.slug : code.toLowerCase();
}

// ============== CITY UTILITIES ==============

export function cityToSlug(city) {
  return city.toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");
}

export function slugToCity(slug) {
  return slug.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

// ============== LOOKUP FUNCTIONS ==============

export function findByUrlSlug(slug) {
  return DOCTOR_MAPPINGS.find(m => m.url_slug === slug) || null;
}

export function getMappingByLabel(label) {
  return DOCTOR_MAPPINGS.find(m => m.browse_label === label) || null;
}

/**
 * Match a plain-English query to the best specialty mapping.
 */
export function findBestMatch(query) {
  if (!query || !query.trim()) return null;
  const q = query.toLowerCase().trim();

  let bestMatch = null;
  let bestScore = 0;

  for (const mapping of DOCTOR_MAPPINGS) {
    let score = 0;
    let matchedOn = "";

    // 1. Exact match on official name, taxonomy, or url_slug
    if (q === mapping.official_name.toLowerCase() || q === mapping.taxonomy_description.toLowerCase() || q === mapping.url_slug) {
      return { match: mapping, score: 100, matchedOn: "official term" };
    }

    // 2. Exact match on a layman term
    for (const term of mapping.layman_terms) {
      if (q === term.toLowerCase()) {
        return { match: mapping, score: 95, matchedOn: term };
      }
    }

    // 3. Exact match on a condition term
    for (const term of mapping.condition_terms) {
      if (q === term.toLowerCase()) {
        return { match: mapping, score: 90, matchedOn: term };
      }
    }

    // 4. Query fully contained in a term (or vice versa)
    for (const term of mapping.layman_terms) {
      const tl = term.toLowerCase();
      if (tl.includes(q) || q.includes(tl)) {
        const s = 80 + Math.min(10, (q.length / tl.length) * 10);
        if (s > score) { score = s; matchedOn = term; }
      }
    }
    for (const term of mapping.condition_terms) {
      const tl = term.toLowerCase();
      if (tl.includes(q) || q.includes(tl)) {
        const s = 75 + Math.min(10, (q.length / tl.length) * 10);
        if (s > score) { score = s; matchedOn = term; }
      }
    }

    // 5. Word overlap scoring
    if (score < 70) {
      const qWords = q.split(/\s+/);
      const allTermWords = [...mapping.layman_terms, ...mapping.condition_terms].join(" ").toLowerCase().split(/\s+/);
      const overlapCount = qWords.filter(w => allTermWords.some(tw => tw.includes(w) || w.includes(tw))).length;
      if (overlapCount > 0) {
        const wordScore = 40 + (overlapCount / qWords.length) * 30;
        if (wordScore > score) { score = wordScore; matchedOn = "keyword match"; }
      }
    }

    // 6. Partial slug/name match
    if (score < 30 && (mapping.official_slug.includes(q.replace(/\s+/g, "-")) || mapping.official_name.toLowerCase().includes(q))) {
      score = 30;
      matchedOn = mapping.official_name;
    }

    if (score > bestScore) {
      bestScore = score;
      bestMatch = { match: mapping, score, matchedOn };
    }
  }

  return bestScore >= 30 ? bestMatch : null;
}

// ============== AUTOCOMPLETE ==============

// Pre-build flat suggestion list for fast filtering
const _suggestions = DOCTOR_MAPPINGS.flatMap(m => {
  const items = [];
  m.layman_terms.forEach(term => items.push({ text: term, type: "term", mapping: m }));
  m.condition_terms.forEach(term => items.push({ text: term, type: "condition", mapping: m }));
  // Also add the browse label and official name as searchable
  items.push({ text: m.browse_label.toLowerCase(), type: "label", mapping: m });
  items.push({ text: m.official_name.toLowerCase(), type: "official", mapping: m });
  return items;
});

/**
 * Return autocomplete suggestions for a partial query.
 * Returns up to `limit` results, deduplicated by specialty.
 */
export function getAutocompleteSuggestions(query, limit = 8) {
  if (!query || query.trim().length < 2) return [];
  const q = query.toLowerCase().trim();

  const scored = _suggestions
    .map(s => {
      let score = 0;
      if (s.text.startsWith(q)) score = 100;
      else if (s.text.includes(q)) score = 70;
      else if (q.split(/\s+/).some(w => s.text.includes(w))) score = 40;
      else return null;

      // Boost layman terms over conditions
      if (s.type === "term") score += 10;
      if (s.type === "label") score += 5;

      return { ...s, score };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score);

  // Deduplicate: one result per specialty
  const seen = new Set();
  const deduped = [];
  for (const item of scored) {
    if (seen.has(item.mapping.url_slug)) continue;
    seen.add(item.mapping.url_slug);
    deduped.push(item);
    if (deduped.length >= limit) break;
  }

  return deduped;
}
